import { request } from './request'

export const filmmakerApi = {
  getById: (id: string) => request(`/filmmakers/${id}`),
  // Ultra-fast profile fetch using pre-aggregated cache (<50ms)
  getFastProfile: (idOrSlug: string, fresh = false) =>
    request(`/fetch-profile/${idOrSlug}${fresh ? '?fresh=true' : ''}`),
  findByUser: (userId: string) => request(`/filmmakers?user_id=${userId}`),
  create: (payload: Record<string, unknown>) =>
    request<{ filmmaker_id: string }>(`/filmmakers`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  update: (id: string, payload: Record<string, unknown>) =>
    request(`/filmmakers/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  replaceLocations: (id: string, locations: string[]) =>
    request(`/filmmakers/${id}/locations`, {
      method: 'POST',
      body: JSON.stringify({ locations }),
    }),
  replaceEquipment: (id: string, equipment: any[]) =>
    request(`/filmmakers/${id}/equipment`, {
      method: 'POST',
      body: JSON.stringify({ equipment }),
    }),
  replaceServices: (id: string, services: any[]) =>
    request(`/filmmakers/${id}/services`, {
      method: 'POST',
      body: JSON.stringify({ services }),
    }),
  // Update a single service (for optimistic uploads with media)
  patchService: (id: string, service: {
    service_id?: string;
    title?: string;
    description?: string;
    category?: string;
    base_rate?: number;
    rate_type?: string;
    service_media?: string | null;
    service_video?: string | null;
  }) =>
    request(`/filmmakers/${id}/service`, {
      method: 'PATCH',
      body: JSON.stringify({ service }),
    }),
  replacePortfolio: (id: string, portfolio: any[]) =>
    request(`/filmmakers/${id}/portfolio`, {
      method: 'POST',
      body: JSON.stringify({ portfolio }),
    }),
  replaceDepartments: (id: string, departments: string[]) =>
    request(`/filmmakers/${id}/departments`, {
      method: 'POST',
      body: JSON.stringify({ departments }),
    }),
  getOverview: (id: string) => request(`/filmmakers/${id}/overview`),
  getOverviewLite: (id: string) => request(`/filmmakers/${id}/overview?lite=true`),
  getSimilarProfiles: (
    id: string,
    options?: { limit?: number; session_id?: string; referrer_id?: string }
  ) => {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.session_id) params.set('session_id', options.session_id);
    if (options?.referrer_id) params.set('referrer_id', options.referrer_id);
    const queryString = params.toString();
    return request(`/filmmakers/${id}/similar${queryString ? `?${queryString}` : ''}`);
  },
}

// Equipment classification types
export interface EquipmentClassification {
  success: boolean;
  name: string;
  canonical_name: string;
  category: string;
  subcategory: string;
  images: string[]; // Array of image URLs for selection
  image_url: string | null; // First image (backward compat)
  cached: boolean;
}

// Equipment API
export const equipmentApi = {
  /**
   * Classify equipment using AI and fetch image
   * Returns category, subcategory, canonical_name, and image_url
   */
  classify: (name: string) =>
    request<EquipmentClassification>('/equipment-classify', {
      method: 'POST',
      body: JSON.stringify({ name }),
    }),

  /**
   * Update a single equipment item (edit name, category, quantity, etc.)
   */
  updateItem: (equipmentId: string, updates: Partial<{
    equipment_name: string;
    category: string;
    subcategory: string;
    quantity: number;
    display_order: number;
  }>) =>
    request(`/equipment/${equipmentId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),

  /**
   * Delete a single equipment item
   */
  deleteItem: (equipmentId: string) =>
    request(`/equipment/${equipmentId}`, {
      method: 'DELETE',
    }),

  /**
   * Upload custom image for equipment item
   */
  uploadImage: (equipmentId: string, imageUrl: string) =>
    request(`/equipment/${equipmentId}/image`, {
      method: 'POST',
      body: JSON.stringify({ image_url: imageUrl }),
    }),

  /**
   * Remove custom image from equipment (revert to AI or blank)
   */
  removeImage: (equipmentId: string) =>
    request(`/equipment/${equipmentId}/image`, {
      method: 'DELETE',
    }),

  /**
   * Update category display order for a filmmaker
   */
  updateCategoryOrder: (filmmakerId: string, categoryOrder: string[]) =>
    request(`/filmmakers/${filmmakerId}/equipment-preferences`, {
      method: 'POST',
      body: JSON.stringify({ category_order: categoryOrder }),
    }),

  /**
   * Get multiple images for equipment selection (from catalog + Google)
   * Free users: 3 API calls/day, Pro: 50/day
   * Catalog lookups are always free
   */
  getImageSelection: (nameOrCanonical: string, count = 3) =>
    request<{
      success: boolean;
      canonical_name: string;
      images: string[];
      source: 'catalog' | 'google';
      new_images_fetched?: number;
      rate_limit?: { allowed: boolean; remaining: number; limit: number };
      message?: string;
    }>(`/equipment-image/select?name=${encodeURIComponent(nameOrCanonical)}&count=${count}`),

  /**
   * Record when a user selects an image (increments popularity) AND cache it to storage
   * This downloads the image server-side (avoiding CORS) and uploads to equipment-images bucket
   */
  recordImageSelection: (canonicalName: string, imageUrl: string) =>
    request<{
      success: boolean;
      storage_path: string | null; // e.g., "equipment-images/sonyfx3.jpg"
      cached: boolean;
      download_failed?: boolean;
      message?: string;
    }>('/equipment-image/select', {
      method: 'POST',
      body: JSON.stringify({ canonical_name: canonicalName, image_url: imageUrl }),
    }),

  /**
   * Report an inappropriate image
   */
  reportImage: (canonicalName: string, imageUrl: string) =>
    request('/equipment-image/report', {
      method: 'POST',
      body: JSON.stringify({ canonical_name: canonicalName, image_url: imageUrl }),
    }),
}

// Service types (following portfolio pattern for media)
export interface ServiceItem {
  service_id?: string;
  title: string;
  department?: string;
  category?: string;
  description?: string;
  base_rate: number;
  rate_type?: string;
  service_type_id?: number;
  // Legacy single image
  service_image?: string;
  // New media fields (following portfolio pattern)
  service_media?: string; // Format: image-paths:path1|path2|path3
  service_video?: string; // YouTube/Vimeo URL or storage path (video-path:path)
  // Resolved URLs (returned from API)
  imageUrl?: string;
  mediaUrls?: string[];
  videoUrl?: string;
}

// Services API
export const servicesApi = {
  /**
   * Get all services for a filmmaker (from overview endpoint)
   */
  getByFilmmaker: (filmmakerId: string) =>
    request<{ services: ServiceItem[] }>(`/filmmakers/${filmmakerId}/overview`).then(
      (res: any) => res.services || []
    ),

  /**
   * Replace all services for a filmmaker
   * Supports media fields: service_media (multiple images), service_video
   */
  replaceAll: (filmmakerId: string, services: ServiceItem[]) =>
    request<{ ok: boolean; services: ServiceItem[] }>(`/filmmakers/${filmmakerId}/services`, {
      method: 'POST',
      body: JSON.stringify({ services }),
    }),

  /**
   * Delete a specific service
   * Pass all remaining services to replaceAll (services use upsert pattern)
   */
  delete: (filmmakerId: string, serviceId: string, currentServices: ServiceItem[]) =>
    servicesApi.replaceAll(
      filmmakerId,
      currentServices.filter((s) => s.service_id !== serviceId)
    ),

  /**
   * Add a new service
   */
  add: (filmmakerId: string, service: Omit<ServiceItem, 'service_id'>, currentServices: ServiceItem[]) =>
    servicesApi.replaceAll(filmmakerId, [...currentServices, service]),

  /**
   * Update a single service using PATCH endpoint
   * This only updates the specified service without affecting others
   */
  update: (
    filmmakerId: string,
    serviceId: string,
    updates: Partial<ServiceItem>,
    _currentServices?: ServiceItem[] // No longer used, kept for backwards compatibility
  ) =>
    request<{ ok: boolean; service: ServiceItem }>(`/filmmakers/${filmmakerId}/service`, {
      method: 'PATCH',
      body: JSON.stringify({
        service: {
          service_id: serviceId,
          ...updates,
        },
      }),
    }),
} 